import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../routes'
import {
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    isSubscribedToPushNotifications,
    isPushNotificationSupported
} from '../utils/pushNotifications'
import { BellIcon, PersonIcon } from '../icons'

type Props = {
    mobile?: boolean
}

const IIUK_BASE_URL = process.env.REACT_APP_IIUK_BASE_URL || 'https://iiuk.org'
const IIUK_SSO_REDIRECT_URL = `${IIUK_BASE_URL}/Pages/Festivals/ivc_redirect.php?auto=1`

export const UserMenu = ({ mobile = false }: Props) => {
    const [isOpen, setIsOpen] = useState(false)
    const [isSubscribed, setIsSubscribed] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const { user, logout, loggingOut } = useAuth()
    const navigate = useNavigate()

    const isSupported = isPushNotificationSupported()

    React.useEffect(() => {
        if (isSupported) {
            checkSubscriptionStatus()
        }
    }, [isSupported])

    // Re-check subscription status when menu opens
    React.useEffect(() => {
        if (isOpen && isSupported) {
            checkSubscriptionStatus()
        }
    }, [isOpen, isSupported])

    const checkSubscriptionStatus = async () => {
        try {
            const subscribed = await isSubscribedToPushNotifications()
            setIsSubscribed(subscribed)
        } catch (err) {
            console.error('Error checking subscription status:', err)
        }
    }

    const handleNotificationToggle = async () => {
        setIsLoading(true)
        setError(null)
        try {
            if (isSubscribed) {
                const success = await unsubscribeFromPushNotifications()
                if (success) {
                    setIsSubscribed(false)
                }
            } else {
                const subscription = await subscribeToPushNotifications()
                if (subscription) {
                    setIsSubscribed(true)
                } else {
                    setError('Failed to enable notifications. Please try again.')
                }
            }
        } catch (err: any) {
            console.error('Error toggling notifications:', err)
            setError(err.message || 'Failed to update notification settings')
        } finally {
            setIsLoading(false)
        }
    }

    const handleLogout = () => {
        setIsOpen(false)
        logout(navigate)
    }

    // // Mobile mode - render inline without dropdown
    // if (mobile) {
    //     return (
    //         <div className="space-y-4">
    //             {/* User Info */}
    //             <div className="px-2 pb-3 border-b border-gray-200 dark:border-gray-700">
    //                 <p className="text-sm font-semibold text-gray-900 dark:text-white">
    //                     {user?.name || 'User'}
    //                 </p>
    //                 <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
    //                     {user?.email || ''}
    //                 </p>
    //             </div>

    //             {/* Notification Settings */}
    //             {isSupported && (
    //                 <div className="px-2 pb-3 border-b border-gray-200 dark:border-gray-700">
    //                     <div className="flex items-center justify-between">
    //                         <div className="flex items-center gap-2">
    //                             <BellIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
    //                             <span className="text-sm font-medium text-gray-900 dark:text-white">
    //                                 Notifications
    //                             </span>
    //                             {!isSubscribed && (
    //                                 <span className="inline-flex items-center justify-center w-2 h-2 bg-warn-500 rounded-full" title="Disabled" />
    //                             )}
    //                         </div>
    //                         <button
    //                             onClick={handleNotificationToggle}
    //                             disabled={isLoading}
    //                             className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
    //                                 isSubscribed ? 'bg-success-500' : 'bg-gray-300 dark:bg-gray-600'
    //                             } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
    //                         >
    //                             <span
    //                                 className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
    //                                     isSubscribed ? 'translate-x-6' : 'translate-x-1'
    //                                 }`}
    //                             />
    //                         </button>
    //                     </div>
    //                     {error && (
    //                         <div className="mt-2 p-2 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded text-xs text-danger-700 dark:text-danger-400 whitespace-pre-line">
    //                             {error}
    //                         </div>
    //                     )}
    //                 </div>
    //             )}

    //             {/* Logout */}
    //             <div className="px-2">
    //                 <button
    //                     onClick={() => logout(navigate)}
    //                     disabled={loggingOut}
    //                     className="w-full text-start text-sm text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 font-medium transition-colors disabled:opacity-50"
    //                 >
    //                     {loggingOut ? 'Logging out...' : 'Logout'}
    //                 </button>
    //             </div>
    //         </div>
    //     )
    // }

    const isIIUK = user?.volunteer.iiuk_registeration === null || user?.volunteer.iiuk_registeration === undefined || user?.volunteer.iiuk_registeration === 1 || user?.volunteer.iiuk_registeration === true

    return (
        <div className="relative">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${mobile ? 'flex items-center gap-3 px-3 py-2 w-full' : 'p-2'}`}
                title="User menu"
            >
                <PersonIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                {mobile && <span className="text-sm font-medium text-gray-900 dark:text-white">{user?.name || 'User'}</span>}
                {/* Badge indicators - show when notifications are OFF */}
                {isSupported && !isSubscribed && (
                    <span className={`w-2 h-2 bg-warn-500 rounded-full ${mobile ? '' : 'absolute top-1.5 end-1.5'}`} title="Notifications disabled" />
                )}
            </button>

            {isOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setIsOpen(false)}
                    />

                    {/* Dropdown Menu */}
                    <div className="absolute end-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                        {/* User Info */}
                        {!mobile && <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                {user?.name || 'User'}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {user?.email || ''}
                            </p>
                        </div>}

                        {/* Notification Settings */}
                        {isSupported && (
                            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <BellIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                                            Notifications
                                        </span>
                                        {!isSubscribed && (
                                            <span className="inline-flex items-center justify-center w-2 h-2 bg-warn-500 rounded-full" title="Disabled" />
                                        )}
                                    </div>
                                    <button
                                        onClick={handleNotificationToggle}
                                        disabled={isLoading}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                            isSubscribed ? 'bg-success-500' : 'bg-gray-300 dark:bg-gray-600'
                                        } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span
                                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                                isSubscribed ? 'translate-x-6' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                                {error && (
                                    <div className="mt-2 p-2 bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded text-xs text-danger-700 dark:text-danger-400 whitespace-pre-line">
                                        {error}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Switch User & Logout */}
                        <div className="px-4 py-3 space-y-2">
                            {isIIUK && <button
                                onClick={() => {
                                    setIsOpen(false)
                                    window.location.href = IIUK_SSO_REDIRECT_URL
                                }}
                                className="w-full text-start text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium transition-colors"
                            >
                                Switch User
                            </button>}
                            <button
                                onClick={handleLogout}
                                disabled={loggingOut}
                                className="w-full text-start text-sm text-danger-600 hover:text-danger-700 dark:text-danger-400 dark:hover:text-danger-300 font-medium transition-colors disabled:opacity-50"
                            >
                                {loggingOut ? 'Logging out...' : 'Logout'}
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}
